export type WheelOption = {
  name: string;
  value: number;
  weight?: number;
};

export type Wheel = {
  id: string;
  name: string;
  options: WheelOption[];
};

// === ALIGNMENT WHEELS ===

export const ALIGNMENT_WHEEL: Wheel = {
  id: "alignment",
  name: "Alignment",
  options: [
    { name: "Hero", value: 0 },
    { name: "Villain", value: 0 },
    { name: "Sidekick", value: 0 },
  ],
};

// Spun only if Alignment landed on "Sidekick".
export const SIDEKICK_OF_WHEEL: Wheel = {
  id: "sidekickOf",
  name: "Sidekick Alignment",
  options: [
    { name: "Hero's Sidekick", value: 0 },
    { name: "Villain's Sidekick", value: 0 },
  ],
};

// === GATE WHEELS ===

// Magic types — reached only via the "Magic" ability category.
export const WHAT_MAGIC_WHEEL: Wheel = {
  id: "magic",
  name: "What Magic?",
  options: [
    { name: "Fire", value: 200 },
    { name: "Ice", value: 200 },
    { name: "Lightning", value: 300 },
    { name: "Earth", value: 300 },
    { name: "Water", value: 300 },
    { name: "Wind", value: 300 },
    { name: "Nature", value: 500 },
    { name: "Light", value: 800 },
    { name: "Darkness", value: 800 },
    { name: "Blood", value: 1500 },
    { name: "Necromancy", value: 2500 },
    { name: "Gravity", value: 4000 },
    { name: "Time", value: 10000 },
    { name: "Space", value: 10000 },
    { name: "Reality Warp", value: 20000 },
    { name: "Chaos", value: 30000 },
    { name: "Divine", value: 40000 },
    { name: "Cosmic", value: 60000 },
  ],
};

// === MAIN WHEELS (spin in this exact order) ===

export const WHEELS: Wheel[] = [
  {
    id: "species",
    name: "Species",
    options: [
      { name: "Human", value: 0 },
      { name: "Mutant", value: 50 },
      { name: "Cyborg", value: 100 },
      { name: "Alien", value: 150 },
      { name: "Vampire", value: 200 },
      { name: "Werewolf", value: 200 },
      { name: "Angel", value: 400 },
      { name: "Demon", value: 400 },
      { name: "Dragonkin", value: 750 },
      { name: "Eldritch Being", value: 1500 },
      { name: "God", value: 5000 },
    ],
  },
  {
    id: "height",
    name: "Height",
    options: [
      { name: "Tiny (30cm)", value: -500 },
      { name: "1m", value: -200 },
      { name: "1.5m", value: 0 },
      { name: "1.8m", value: 100 },
      { name: "2m", value: 250 },
      { name: "2.5m", value: 500 },
      { name: "3m", value: 750 },
      { name: "5m", value: 1500 },
      { name: "10m", value: 3000 },
      { name: "20m", value: 6000 },
      { name: "50m", value: 12000 },
      { name: "100m", value: 25000 },
      { name: "Can Change Height", value: 15000 },
      { name: "Infinite Size", value: 60000 },
    ],
  },
  {
    id: "gender",
    name: "Gender",
    options: [
      { name: "Male", value: 0 },
      { name: "Female", value: 0 },
      { name: "Non-binary", value: 0 },
      { name: "Android", value: 1000 },
      { name: "Shapeshifter", value: 3000 },
      { name: "Unknown Entity", value: 6000 },
      { name: "Beyond Gender", value: 12000 },
      { name: "Biblically Accurate", value: 25000 },
    ],
  },
  {
    id: "age",
    name: "Age",
    options: [
      { name: "Baby", value: -1000 },
      { name: "Kid", value: -300 },
      { name: "Teen", value: 0 },
      { name: "Adult", value: 100 },
      { name: "Ancient", value: 1000 },
      { name: "1000 Years", value: 3000 },
      { name: "10000 Years", value: 8000 },
      { name: "Ageless", value: 15000 },
      { name: "Older Than Time", value: 30000 },
    ],
  },
  {
    id: "appearance",
    name: "Appearance",
    options: [
      { name: "Ugly", value: -1000 },
      { name: "Average", value: 0 },
      { name: "Cute", value: 200 },
      { name: "Hot", value: 600 },
      { name: "Beautiful", value: 1500 },
      { name: "Model", value: 3000 },
      { name: "Intimidating", value: 3000 },
      { name: "Eldritch Horror", value: 8000 },
      { name: "Biblically Accurate", value: 15000 },
      { name: "Cannot Be Perceived", value: 30000 },
    ],
  },
  {
    id: "strength",
    name: "Strength",
    options: [
      { name: "Child", value: -100 },
      { name: "Human", value: 0 },
      { name: "Athlete", value: 20 },
      { name: "Peak Human", value: 50 },
      { name: "Wall Level", value: 100 },
      { name: "Building Level", value: 200 },
      { name: "City Block", value: 300 },
      { name: "Town Level", value: 500 },
      { name: "City Level", value: 800 },
      { name: "Mountain Level", value: 1500 },
      { name: "Island Level", value: 2500 },
      { name: "Country Level", value: 4000 },
      { name: "Continent Level", value: 6000 },
      { name: "Moon Level", value: 9000 },
      { name: "Planet Level", value: 15000 },
      { name: "Star Level", value: 22000 },
      { name: "Solar System Level", value: 32000 },
      { name: "Galaxy Level", value: 45000 },
      { name: "Universal", value: 65000 },
      { name: "Multiversal", value: 90000 },
      { name: "Outerversal", value: 150000 },
    ],
  },
  {
    id: "speed",
    name: "Speed",
    options: [
      { name: "Crippled", value: -200 },
      { name: "Walking", value: 0 },
      { name: "Running", value: 25 },
      { name: "Olympic", value: 50 },
      { name: "Car", value: 100 },
      { name: "Bullet", value: 200 },
      { name: "Supersonic", value: 400 },
      { name: "Hypersonic", value: 700 },
      { name: "Lightning", value: 1200 },
      { name: "Light Speed", value: 2000 },
      { name: "FTL", value: 3500 },
      { name: "MFTL", value: 6000 },
      { name: "Instant Teleportation", value: 10000 },
    ],
  },
  {
    id: "durability",
    name: "Durability",
    options: [
      { name: "Glass Cannon", value: -300 },
      { name: "Human", value: 0 },
      { name: "Bullet Resistant", value: 100 },
      { name: "Bulletproof", value: 300 },
      { name: "Building Level", value: 600 },
      { name: "City Level", value: 1500 },
      { name: "Mountain Level", value: 3000 },
      { name: "Planet Level", value: 6000 },
      { name: "Universal", value: 12000 },
      { name: "Immortal", value: 8000 },
      { name: "Invincible", value: 20000 },
    ],
  },
  {
    id: "stamina",
    name: "Stamina",
    options: [
      { name: "5 Minutes", value: -1000 },
      { name: "1 Hour", value: 0 },
      { name: "12 Hours", value: 400 },
      { name: "1 Day", value: 1000 },
      { name: "1 Week", value: 3000 },
      { name: "1 Month", value: 6000 },
      { name: "Never Sleeps", value: 10000 },
      { name: "Infinite Stamina", value: 30000 },
    ],
  },
  {
    id: "intelligence",
    name: "Intelligence",
    options: [
      { name: "Idiot", value: -300 },
      { name: "Average", value: 0 },
      { name: "Smart", value: 100 },
      { name: "Gifted", value: 250 },
      { name: "Genius", value: 600 },
      { name: "Master Strategist", value: 1500 },
      { name: "Scientific Prodigy", value: 2500 },
      { name: "Supercomputer Mind", value: 5000 },
      { name: "Omniscient", value: 15000 },
    ],
  },
  {
    id: "healingFactor",
    name: "Healing Factor",
    options: [
      { name: "Healing Hurts", value: -500 },
      { name: "Heals Others Instead", value: -1000 },
      { name: "Normal", value: 0 },
      { name: "Fast", value: 500 },
      { name: "Regeneration", value: 2000 },
      { name: "Regrow Limbs", value: 4000 },
      { name: "Heal Instantly", value: 8000 },
      { name: "Cannot Die Naturally", value: 15000 },
      { name: "Rebuild From One Cell", value: 25000 },
      { name: "Comes Back Stronger", value: 35000 },
    ],
  },
  {
    id: "combatSkill",
    name: "Combat Skill",
    options: [
      { name: "Can't Fight", value: -1000 },
      { name: "Street Fighter", value: 0 },
      { name: "Military", value: 500 },
      { name: "Martial Artist", value: 1000 },
      { name: "Weapons Expert", value: 1500 },
      { name: "Assassin", value: 2500 },
      { name: "Sword Master", value: 4000 },
      { name: "Grandmaster", value: 6000 },
      { name: "Legendary Warrior", value: 10000 },
      { name: "Perfect Fighter", value: 20000 },
      { name: "Knows Every Martial Art", value: 35000 },
    ],
  },
  {
    id: "companion",
    name: "Companion",
    options: [
      { name: "None", value: 0 },
      { name: "Cat", value: 100 },
      { name: "Dog", value: 150 },
      { name: "Fox", value: 150 },
      { name: "Wolf", value: 300 },
      { name: "Bear", value: 400 },
      { name: "Tiger", value: 500 },
      { name: "AI Companion", value: 500 },
      { name: "Phoenix", value: 1500 },
      { name: "Dragon", value: 3000 },
      { name: "Spirit Guardian", value: 2000 },
      { name: "Hydra", value: 4000 },
      { name: "Army", value: 6000 },
      { name: "Kraken", value: 6000 },
      { name: "Kaiju", value: 12000 },
      { name: "Godzilla", value: 15000 },
      { name: "Another Version Of Yourself", value: 20000 },
      { name: "Biblically Accurate Angel", value: 25000 },
      { name: "The Moon", value: 50000 },
    ],
  },
  {
    id: "weakness",
    name: "Weakness",
    options: [
      { name: "One Hit And Dead", value: -15000 },
      { name: "Pride", value: -2000 },
      { name: "Love", value: -2000 },
      { name: "Fear", value: -2000 },
      { name: "Sunlight", value: -1500 },
      { name: "Magic", value: -1500 },
      { name: "Fire", value: -800 },
      { name: "Water", value: -800 },
      { name: "Sleep", value: -500 },
      { name: "Rubber Ducks", value: -200 },
      { name: "Cats", value: -200 },
      { name: "None", value: 3000 },
    ],
  },
  {
    id: "job",
    name: "Job",
    options: [
      { name: "Tax Collector", value: -1000 },
      { name: "Unemployed", value: -500 },
      { name: "Intern", value: -300 },
      { name: "Farmer", value: 0 },
      { name: "Streamer", value: 150 },
      { name: "Knight", value: 400 },
      { name: "Blacksmith", value: 300 },
      { name: "Doctor", value: 600 },
      { name: "Detective", value: 500 },
      { name: "Pirate", value: 600 },
      { name: "Professional Gambler", value: 600 },
      { name: "Mercenary", value: 800 },
      { name: "CEO", value: 800 },
      { name: "King", value: 1500 },
      { name: "Wizard", value: 1500 },
      { name: "Cult Leader", value: 2000 },
      { name: "Hitman", value: 2000 },
      { name: "Emperor", value: 3000 },
      { name: "Time Traveler", value: 8000 },
      { name: "Cosmic Janitor", value: 15000 },
    ],
  },
  {
    id: "mentalState",
    name: "Mental State",
    options: [
      { name: "Schizophrenic", value: -1000 },
      { name: "Paranoid", value: -500 },
      { name: "Sleep Deprived", value: -200 },
      { name: "Coffee Addict", value: 0 },
      { name: "Just Here For The Paycheck", value: 0 },
      { name: "Gremlin", value: 200 },
      { name: "Angry", value: 500 },
      { name: "Perfectly Sane", value: 500 },
      { name: "Crackhead Energy", value: 800 },
      { name: "Cannot Feel Fear", value: 1500 },
      { name: "Cannot Feel Pain", value: 1500 },
      { name: "Pure Good", value: 1500 },
      { name: "Chaotic", value: 2000 },
      { name: "Pure Evil", value: 3000 },
      { name: "Too Dumb To Die", value: 5000 },
    ],
  },
  {
    id: "plotArmor",
    name: "Plot Armor",
    options: [
      { name: "NPC", value: -3000 },
      { name: "Background Character", value: -1000 },
      { name: "Side Character", value: 0 },
      { name: "Rival", value: 1000 },
      { name: "Anti Hero", value: 3000 },
      { name: "Main Character", value: 8000 },
      { name: "Chosen One", value: 20000 },
      { name: "Author's Favorite", value: 50000 },
      { name: "Literal Plot Device", value: 100000 },
    ],
  },
];

// === CHAOS WHEELS (random trigger or manual, 5 spin cooldown) ===

export const CHAOS_WHEELS: Wheel[] = [
  {
    id: "buff",
    name: "Buff",
    options: [
      { name: "Double Your Highest Stat", value: 0 },
      { name: "Remove One Weakness", value: 0 },
      { name: "Gain Another Ability", value: 0 },
      { name: "Gain Another Transformation", value: 0 },
      { name: "Species Evolves", value: 0 },
      { name: "Gain Another Companion", value: 0 },
    ],
  },
  {
    id: "nerf",
    name: "Nerf",
    options: [
      { name: "Lose Your Weapon", value: 0 },
      { name: "Lose Your Magic", value: 0 },
      { name: "Halve Your Speed", value: 0 },
      { name: "Gain Another Weakness", value: 0 },
      { name: "Lose Your Companion", value: 0 },
      { name: "Powers Malfunction", value: 0 },
    ],
  },
  {
    id: "lore",
    name: "Lore",
    options: [
      { name: "Secret Royal Bloodline", value: 1500 },
      { name: "Ancient Prophecy", value: 3000 },
      { name: "Raised By Villains", value: 1500 },
      { name: "Last Of Your Species", value: 5000 },
      { name: "Amnesia", value: 800 },
      { name: "Sealed Evil Inside You", value: 6000 },
      { name: "Accidental Experiment", value: 3000 },
      { name: "Betrayed By Best Friend", value: 1500 },
      { name: "Escaped Hell", value: 6000 },
      { name: "Born During An Eclipse", value: 5000 },
    ],
  },
  {
    id: "achievement",
    name: "Achievement",
    options: [
      { name: "Won A Tournament", value: 2000 },
      { name: "Ended A War", value: 6000 },
      { name: "Saved The World", value: 15000 },
      { name: "Destroyed A Planet", value: 25000 },
      { name: "Killed A God", value: 40000 },
      { name: "Escaped Hell", value: 20000 },
      { name: "Survived The Apocalypse", value: 25000 },
      { name: "Defeated Your Future Self", value: 40000 },
      { name: "Beat Death In A Fight", value: 70000 },
      { name: "Destroyed A Universe", value: 100000 },
    ],
  },
];

// === ABILITY SYSTEM (runs after all main wheels are complete) ===

// Step 1: how many abilities the character gets. Points awarded immediately.
export const ABILITY_COUNT_WHEEL: Wheel = {
  id: "abilityCount",
  name: "Ability Count",
  options: [
    { name: "No Abilities", value: 0 },
    { name: "1 Ability", value: 200 },
    { name: "2 Abilities", value: 500 },
    { name: "3 Abilities", value: 900 },
    { name: "4 Abilities", value: 1500 },
    { name: "5 Abilities", value: 2500 },
    { name: "6 Abilities", value: 4000 },
    { name: "7 Abilities", value: 6000 },
    { name: "8 Abilities", value: 9000 },
    { name: "9 Abilities", value: 13000 },
    { name: "10 Abilities", value: 20000 },
  ],
};

// Parses "No Abilities" -> 0, "3 Abilities" -> 3, etc.
export function getAbilityCount(name: string): number {
  if (name === "No Abilities") return 0;
  return parseInt(name, 10) || 0;
}

// True when a result came from one of the main scoring wheels (Species, the
// stats, Weakness, Companion, …). Used by the single-wheel reroll to offer a
// targeted re-spin only on those rows — ability/crown/consolation/fight rows
// (which aren't in WHEELS) are intentionally not re-spinnable.
export function isMainWheelName(name: string): boolean {
  return WHEELS.some((w) => w.name === name);
}

// Consolation flow, triggered when "No Abilities" is rolled.
export const CONSOLATION_GATE_WHEEL: Wheel = {
  id: "consolationGate",
  name: "Do You At Least Get A Weapon?",
  options: [
    { name: "Yes", value: 0 },
    { name: "No", value: 0 },
  ],
};

// Step 2: weighted category roll (run once per ability). Stacking is fully
// allowed — the same category can be landed on multiple times in a run.
export const ABILITY_CATEGORY_WHEEL: Wheel = {
  id: "abilityCategory",
  name: "Ability Category",
  options: [
    { name: "Magic", value: 0, weight: 6 },
    { name: "Physical", value: 0, weight: 13 },
    { name: "Energy", value: 0, weight: 11 },
    { name: "Psychic", value: 0, weight: 10 },
    { name: "Space-Time", value: 0, weight: 9 },
    { name: "Biological", value: 0, weight: 9 },
    { name: "Summoning", value: 0, weight: 8 },
    { name: "Dumb", value: 0, weight: 8 },
    { name: "Transformation", value: 0, weight: 8 },
    { name: "Weapon", value: 0, weight: 7 },
    { name: "Cursed", value: 0, weight: 7 },
    { name: "Cosmic", value: 0, weight: 4 },
  ],
};

// Step 3: spin within the landed category.

export const PHYSICAL_ABILITIES_WHEEL: Wheel = {
  id: "physicalAbility",
  name: "Physical Ability",
  options: [
    { name: "Super Strength", value: 600 },
    { name: "Super Speed", value: 600 },
    { name: "Flight", value: 400 },
    { name: "Super Jump", value: 300 },
    { name: "Wall Crawling", value: 200 },
    { name: "Elastic Body", value: 300 },
    { name: "Size Change", value: 600 },
    { name: "Enhanced Senses", value: 300 },
    { name: "Camouflage", value: 400 },
    { name: "Natural Weapons", value: 300 },
    { name: "Poison", value: 400 },
    { name: "Electric Body", value: 500 },
    { name: "Stone Skin", value: 600 },
    { name: "Wings", value: 400 },
    { name: "Extra Arms", value: 400 },
    { name: "Extra Eyes", value: 300 },
    { name: "Perfect Balance", value: 200 },
    { name: "Night Vision", value: 200 },
    { name: "Water Breathing", value: 200 },
    { name: "Extreme Flexibility", value: 200 },
  ],
};

export const ENERGY_ABILITIES_WHEEL: Wheel = {
  id: "energyAbility",
  name: "Energy Ability",
  options: [
    { name: "Fire", value: 600 },
    { name: "Ice", value: 600 },
    { name: "Lightning", value: 800 },
    { name: "Light", value: 800 },
    { name: "Darkness", value: 800 },
    { name: "Wind", value: 600 },
    { name: "Earth", value: 600 },
    { name: "Water", value: 600 },
    { name: "Plasma", value: 1200 },
    { name: "Sound", value: 800 },
    { name: "Radiation", value: 1200 },
    { name: "Magnetism", value: 1200 },
    { name: "Gravity", value: 2000 },
    { name: "Heat", value: 600 },
    { name: "Cold", value: 600 },
    { name: "Crystal", value: 600 },
    { name: "Smoke", value: 400 },
    { name: "Ash", value: 500 },
    { name: "Sand", value: 500 },
    { name: "Metal", value: 800 },
  ],
};

export const PSYCHIC_ABILITIES_WHEEL: Wheel = {
  id: "psychicAbility",
  name: "Psychic Ability",
  options: [
    { name: "Telepathy", value: 1000 },
    { name: "Telekinesis", value: 1000 },
    { name: "Mind Control", value: 3000 },
    { name: "Illusions", value: 2000 },
    { name: "Dream Walking", value: 2500 },
    { name: "Memory Manipulation", value: 3000 },
    { name: "Emotion Control", value: 2500 },
    { name: "Future Sight", value: 5000 },
    { name: "Precognition", value: 4000 },
    { name: "Astral Projection", value: 1500 },
    { name: "Soul Sight", value: 2000 },
    { name: "Fear Aura", value: 1500 },
    { name: "Calm Aura", value: 800 },
    { name: "Psychic Blast", value: 1500 },
    { name: "Empathy", value: 800 },
    { name: "Mental Barrier", value: 1000 },
  ],
};

export const SPACE_TIME_ABILITIES_WHEEL: Wheel = {
  id: "spaceTimeAbility",
  name: "Space-Time Ability",
  options: [
    { name: "Teleportation", value: 2000 },
    { name: "Portal Creation", value: 3000 },
    { name: "Time Stop", value: 8000 },
    { name: "Time Slow", value: 5000 },
    { name: "Time Rewind", value: 8000 },
    { name: "Dimension Travel", value: 5000 },
    { name: "Pocket Dimension", value: 4000 },
    { name: "Space Warp", value: 5000 },
    { name: "Clone", value: 2500 },
    { name: "Timeline", value: 12000 },
    { name: "Future Travel", value: 10000 },
    { name: "Past Travel", value: 10000 },
    { name: "Spatial Cutting", value: 3000 },
  ],
};

export const BIOLOGICAL_ABILITIES_WHEEL: Wheel = {
  id: "biologicalAbility",
  name: "Biological Ability",
  options: [
    { name: "Healing", value: 1000 },
    { name: "Regeneration", value: 3000 },
    { name: "Adaptation", value: 5000 },
    { name: "Evolution", value: 8000 },
    { name: "Shapeshifting", value: 3000 },
    { name: "Body Manipulation", value: 2500 },
    { name: "Bone Manipulation", value: 1500 },
    { name: "Blood Manipulation", value: 2000 },
    { name: "Plant Manipulation", value: 800 },
    { name: "Animal Communication", value: 600 },
    { name: "Virus Control", value: 2500 },
    { name: "Poison Generation", value: 1500 },
    { name: "Mutation", value: 2000 },
    { name: "Immortality", value: 10000 },
  ],
};

export const SUMMONING_ABILITIES_WHEEL: Wheel = {
  id: "summoningAbility",
  name: "Summoning Ability",
  options: [
    { name: "Summon Weapons", value: 800 },
    { name: "Summon Animals", value: 600 },
    { name: "Summon Spirits", value: 1500 },
    { name: "Summon Monsters", value: 2000 },
    { name: "Summon Dragons", value: 3000 },
    { name: "Summon Undead", value: 2500 },
    { name: "Summon Elementals", value: 2000 },
    { name: "Summon Golems", value: 1500 },
    { name: "Summon Shadows", value: 1200 },
    { name: "Summon Clones", value: 2500 },
    { name: "Summon Giants", value: 2500 },
    { name: "Summon Constructs", value: 1200 },
  ],
};

// All negative — landing here is a punishment.
export const DUMB_ABILITIES_WHEEL: Wheel = {
  id: "dumbAbility",
  name: "Dumb Ability",
  options: [
    { name: "Can Perfectly Peel Oranges", value: -25 },
    { name: "Can Always Find Parking", value: -50 },
    { name: "Can Summon Bread", value: -75 },
    { name: "Can Summon A Chair", value: -75 },
    { name: "Can Summon A Brick", value: -100 },
    { name: "Never Trips", value: -100 },
    { name: "Always Knows Where The TV Remote Is", value: -150 },
    { name: "Can Talk To Fish", value: -150 },
    { name: "Can Summon Ducks", value: -150 },
    { name: "Never Loses At Rock Paper Scissors", value: -200 },
    { name: "Can Spawn Traffic Cones", value: -200 },
    { name: "Can Make Anyone Sneeze", value: -250 },
    { name: "Can Turn Invisible Only When Nobody Is Looking", value: -300 },
    { name: "Perfect WiFi", value: -400 },
    { name: "Can Teleport But Only Backwards", value: -500 },
    { name: "Can Speak Every Language Except The Local One", value: -600 },
    { name: "Can Throw Pennies At Mach 10", value: -800 },
    { name: "Infinite Pocket Storage", value: -1000 },
  ],
};

// Stackable — multiple transformations are allowed.
export const TRANSFORMATION_ABILITIES_WHEEL: Wheel = {
  id: "transformationAbility",
  name: "Transformation",
  options: [
    { name: "Transformation Kills You Slowly", value: -2000 },
    { name: "Unstable Form", value: -1500 },
    { name: "Incomplete Transformation", value: -1000 },
    { name: "None", value: 0 },
    { name: "Rage Mode", value: 500 },
    { name: "Beast Form", value: 1000 },
    { name: "Super Form", value: 3000 },
    { name: "Ultimate Form", value: 7000 },
    { name: "God Form", value: 15000 },
    { name: "True Form", value: 25000 },
    { name: "Perfect Form", value: 40000 },
    { name: "Final Form", value: 60000 },
  ],
};

// Stackable — multiple weapons are allowed.
export const WEAPON_ABILITIES_WHEEL: Wheel = {
  id: "weaponAbility",
  name: "Weapon",
  options: [
    { name: "Weapon That Hates You", value: -3000 },
    { name: "Cursed Weapon", value: -1500 },
    { name: "Broken Weapon", value: -500 },
    { name: "Bare Hands", value: 0 },
    { name: "Knife", value: 50 },
    { name: "Sword", value: 200 },
    { name: "Spear", value: 200 },
    { name: "Bow", value: 200 },
    { name: "Gun", value: 400 },
    { name: "Hammer", value: 400 },
    { name: "Magic Staff", value: 800 },
    { name: "Energy Blade", value: 2000 },
    { name: "Living Weapon", value: 5000 },
    { name: "Planet Killer Sword", value: 15000 },
    { name: "Conceptual Weapon", value: 40000 },
  ],
};

// Stackable — multiple curses are allowed.
export const CURSED_ABILITIES_WHEEL: Wheel = {
  id: "cursedAbility",
  name: "Cursed",
  options: [
    { name: "You're Hunted By A Goose", value: -1000 },
    { name: "Everyone Hears Your Thoughts", value: -500 },
    { name: "Gravity Hates You", value: -300 },
    { name: "Your Reflection Lies", value: -200 },
    { name: "Everything You Touch Becomes Cheese", value: -100 },
    { name: "Luck Alternates Between Perfect And Horrible", value: 0 },
    { name: "Your Shadow Is Alive", value: 300 },
    { name: "You Randomly Swap Bodies", value: 200 },
    { name: "You Hear Everyone's Thoughts", value: 500 },
    { name: "Everything You Touch Explodes", value: 500 },
  ],
};

// Rare (weight 4) — massive rewards.
export const COSMIC_ABILITIES_WHEEL: Wheel = {
  id: "cosmicAbility",
  name: "Cosmic Ability",
  options: [
    { name: "Probability Manipulation", value: 15000 },
    { name: "Reality Manipulation", value: 30000 },
    { name: "Matter Manipulation", value: 20000 },
    { name: "Energy Manipulation", value: 15000 },
    { name: "Concept Manipulation", value: 50000 },
    { name: "Existence Erasure", value: 40000 },
    { name: "Power Copy", value: 25000 },
    { name: "Power Steal", value: 30000 },
    { name: "Fate Manipulation", value: 30000 },
    { name: "Void Manipulation", value: 25000 },
    { name: "Creation", value: 40000 },
    { name: "Cosmic Awareness", value: 15000 },
  ],
};

// Maps a landed ABILITY_CATEGORY_WHEEL option name to its ability wheel.
export const ABILITY_CATEGORY_WHEELS: Record<string, Wheel> = {
  Magic: WHAT_MAGIC_WHEEL,
  Physical: PHYSICAL_ABILITIES_WHEEL,
  Energy: ENERGY_ABILITIES_WHEEL,
  Psychic: PSYCHIC_ABILITIES_WHEEL,
  "Space-Time": SPACE_TIME_ABILITIES_WHEEL,
  Biological: BIOLOGICAL_ABILITIES_WHEEL,
  Summoning: SUMMONING_ABILITIES_WHEEL,
  Dumb: DUMB_ABILITIES_WHEEL,
  Transformation: TRANSFORMATION_ABILITIES_WHEEL,
  Weapon: WEAPON_ABILITIES_WHEEL,
  Cursed: CURSED_ABILITIES_WHEEL,
  Cosmic: COSMIC_ABILITIES_WHEEL,
};

// Step 4: power mastery, spun once after all abilities are collected (or
// once after the consolation weapon, if "No Abilities" + weapon was rolled).
export const POWER_MASTERY_WHEEL: Wheel = {
  id: "powerMastery",
  name: "Power Mastery",
  options: [
    { name: "0% Power Hurts You", value: -3000 },
    { name: "10% Barely Usable", value: -1500 },
    { name: "20%", value: 0 },
    { name: "30%", value: 300 },
    { name: "40%", value: 800 },
    { name: "50%", value: 2000 },
    { name: "60%", value: 4000 },
    { name: "70%", value: 7000 },
    { name: "80%", value: 12000 },
    { name: "90%", value: 20000 },
    { name: "100% Perfect Control", value: 35000 },
    { name: "110% Can Exceed Limits", value: 55000 },
    { name: "Awakened", value: 80000 },
    { name: "Transcendent", value: 120000 },
    { name: "Absolute Mastery", value: 200000 },
  ],
};

export type RankThreshold = {
  min: number;
  label: string;
};

export const RANK_THRESHOLDS: RankThreshold[] = [
  { min: 25000000, label: "The Author Needs To Nerf You" },
  { min: 10000000, label: "TikTok Powerscaler" },
  { min: 5000000, label: "Boundless" },
  { min: 2000000, label: "Outerversal" },
  { min: 1000000, label: "Multiversal" },
  { min: 500000, label: "Universal" },
  { min: 250000, label: "Galactic" },
  { min: 100000, label: "Planetary" },
  { min: 40000, label: "World Threat" },
  { min: 15000, label: "National Threat" },
  { min: 5000, label: "City Threat" },
  { min: 1000, label: "Street Hero" },
  { min: -Infinity, label: "NPC" },
];

// === BONUS: PICK A FIGHT (optional, triggered from the results screen) ===

export const FIGHT_OPPONENT_WHEEL: Wheel = {
  id: "fightOpponent",
  name: "Who Do You Fight?",
  options: [
    "Goku",
    "Naruto Uzumaki",
    "Ichigo Kurosaki",
    "Monkey D. Luffy",
    "Edward Elric",
    "Tanjiro Kamado",
    "Izuku Midoriya",
    "Gon Freecss",
    "Spike Spiegel",
    "Lelouch vi Britannia",
    "Yusuke Urameshi",
    "Natsu Dragneel",
    "Sailor Moon",
    "Ash Ketchum",
    "Kirito",
    "Kenshin Himura",
    "Simon",
    "Aang",
    "Korra",
    "Saitama",
    "Ryuko Matoi",
    "Kaneki Ken",
    "Inuyasha",
    "Batman",
    "Spider-Man",
    "Superman",
    "Optimus Prime",
    "He-Man",
    "Leonardo",
    "Lion-O",
    "Finn the Human",
    "Steven Universe",
    "Kim Possible",
    "Ben Tennyson",
    "Danny Phantom",
    "Samurai Jack",
    "Blossom",
    "Bubbles",
    "Buttercup",
    "Dipper Pines",
    "Star Butterfly",
    "Hiro Hamada",
    "Mikasa Ackerman",
    "Erza Scarlet",
    "Maka Albarn",
    "Jake Long",
    "Captain America",
    "Iron Man",
    "Mega Man",
    "Wander",
    "Frieza",
    "Cell",
    "Majin Buu",
    "Madara Uchiha",
    "Orochimaru",
    "Pain",
    "Aizen Sosuke",
    "Blackbeard",
    "Doflamingo",
    "Father",
    "Muzan Kibutsuji",
    "All For One",
    "Shigaraki Tomura",
    "Meruem",
    "Hisoka",
    "Light Yagami",
    "Ryuk",
    "Zeref Dragneel",
    "Dio Brando",
    "Yoshikage Kira",
    "Johan Liebert",
    "Griffith",
    "Esdeath",
    "Shishio Makoto",
    "Younger Toguro",
    "Joker",
    "Lex Luthor",
    "Green Goblin",
    "Doctor Doom",
    "Megatron",
    "Shredder",
    "Skeletor",
    "Mumm-Ra",
    "The Lich",
    "Bill Cipher",
    "Aku",
    "Vlad Plasmius",
    "Vilgax",
    "Fire Lord Ozai",
    "Hades",
    "Maleficent",
    "Ursula",
    "Jafar",
    "Scar",
    "Queen Beryl",
    "Ragyo Kiryuin",
    "Gendo Ikari",
    "Magneto",
    "Apocalypse",
    "Zeke Yeager",
    "Krillin",
    "Bulma",
    "Piccolo",
    "Sakura Haruno",
    "Rock Lee",
    "Alphonse Elric",
    "Zenitsu Agatsuma",
    "Inosuke Hashibira",
    "Killua Zoldyck",
    "Orihime Inoue",
    "Usopp",
    "Chopper",
    "Pikachu",
    "Robin",
    "Bumblebee",
    "Michelangelo",
    "Donatello",
    "Raphael",
    "Jake the Dog",
    "BMO",
    "Ron Stoppable",
    "Rufus",
    "Gwen Tennyson",
    "Tucker Foley",
    "Sam Manson",
    "Sokka",
    "Katara",
    "Toph Beifong",
    "Mushu",
    "Jiminy Cricket",
    "Timon",
    "Pumbaa",
    "GIR",
    "Tenya Iida",
    "Ochaco Uraraka",
    "Connie Maheswaran",
    "Garnet",
    "Pearl",
    "Mabel Pines",
    "Soos",
    "Leorio Paradinight",
    "Nami",
    "Sebastian",
    "Flounder",
    "Brock",
    "Misty",
    "Winry Rockbell",
    "Asuna",
    "Meowth",
    "Amethyst",
  ].map((name) => ({ name, value: 0 })),
};

export const FIGHT_OUTCOME_WHEEL: Wheel = {
  id: "fightOutcome",
  name: "Do You Win The Fight?",
  options: [
    { name: "Yes", value: 0 },
    { name: "No", value: 0 },
  ],
};

// === CROWN MECHANIC (triggers at 450,000+ total points) ===

export const CROWN_THRESHOLD = 450000;

export const CROWN_WHEEL: Wheel = {
  id: "crownBonus",
  name: "Double Or Nothing",
  options: [
    { name: "2x Strength", value: 0 },
    { name: "2x Speed", value: 0 },
    { name: "2x Durability", value: 0 },
    { name: "2x Companion", value: 0 },
    { name: "2x Best Ability", value: 0 },
    { name: "You've Been Struck By A Truck", value: -5000 },
  ],
};

// Maps a CROWN_WHEEL option name to the results entry whose value it doubles.
// "2x Best Ability" is handled specially (doubles the highest-value ability
// result instead of a fixed wheel).
export const CROWN_STAT_MAP: Record<string, string> = {
  "2x Strength": "Strength",
  "2x Speed": "Speed",
  "2x Durability": "Durability",
  "2x Companion": "Companion",
};
